/**
 * Modal Management System
 * Centralized modal state management for better performance and UX
 * Inspired by production-grade applications
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Modal } from 'antd';

interface ModalConfig {
  title?: ReactNode;
  content?: ReactNode;
  width?: number | string;
  okText?: string;
  cancelText?: string;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  afterClose?: () => void;
  maskClosable?: boolean;
  destroyOnHidden?: boolean;
  footer?: ReactNode | null;
  [key: string]: any; // Allow additional antd Modal props
}

interface ModalState extends ModalConfig {
  id: string;
  visible: boolean;
  loading?: boolean;
}

interface ModalContextType {
  openModal: (config: ModalConfig) => string;
  closeModal: (id: string) => void;
  updateModal: (id: string, updates: Partial<ModalConfig>) => void;
  closeAllModals: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

let modalIdCounter = 0;

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<ModalState[]>([]);

  const openModal = useCallback((config: ModalConfig): string => {
    const id = `modal-${++modalIdCounter}-${Date.now()}`;
    const newModal: ModalState = {
      ...config,
      id,
      visible: true,
      loading: false,
    };

    setModals((prev) => [...prev, newModal]);
    return id;
  }, []);

  const closeModal = useCallback((id: string) => {
    setModals((prev) =>
      prev.map((modal) =>
        modal.id === id
          ? { ...modal, visible: false }
          : modal
      )
    );

    // Remove modal after animation completes
    setTimeout(() => {
      setModals((prev) => prev.filter((modal) => modal.id !== id));
    }, 300);
  }, []);

  const updateModal = useCallback((id: string, updates: Partial<ModalConfig>) => {
    setModals((prev) =>
      prev.map((modal) =>
        modal.id === id ? { ...modal, ...updates } : modal
      )
    );
  }, []);

  const closeAllModals = useCallback(() => {
    setModals((prev) =>
      prev.map((modal) => ({ ...modal, visible: false }))
    );
    setTimeout(() => {
      setModals([]);
    }, 300);
  }, []);

  const handleOk = useCallback(async (modal: ModalState) => {
    if (modal.loading) return;

    updateModal(modal.id, { loading: true });

    try {
      if (modal.onOk) {
        await modal.onOk();
      }
      closeModal(modal.id);
    } catch (error) {
      console.error('Modal onOk error:', error);
      updateModal(modal.id, { loading: false });
    }
  }, [closeModal, updateModal]);

  const handleCancel = useCallback((modal: ModalState) => {
    if (modal.onCancel) {
      modal.onCancel();
    }
    closeModal(modal.id);
  }, [closeModal]);

  const handleAfterClose = useCallback((modal: ModalState) => {
    if (modal.afterClose) {
      modal.afterClose();
    }
  }, []);

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        updateModal,
        closeAllModals,
      }}
    >
      {children}
      {modals.map((modal) => (
        <Modal
          key={modal.id}
          title={modal.title}
          open={modal.visible}
          width={modal.width}
          okText={modal.okText}
          cancelText={modal.cancelText}
          confirmLoading={modal.loading}
          maskClosable={modal.maskClosable ?? true}
          destroyOnHidden={modal.destroyOnHidden ?? false}
          footer={modal.footer}
          onOk={() => handleOk(modal)}
          onCancel={() => handleCancel(modal)}
          afterClose={() => handleAfterClose(modal)}
          {...Object.fromEntries(
            Object.entries(modal).filter(
              ([key]) =>
                !['id', 'visible', 'loading', 'onOk', 'onCancel', 'afterClose'].includes(key)
            )
          )}
        >
          {modal.content}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
};

/**
 * Hook to use modal manager
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

/**
 * Convenience hooks for common modal types
 */
export const useConfirmModal = () => {
  const { openModal, closeModal, updateModal } = useModal();

  const confirm = useCallback(
    (config: ModalConfig): Promise<boolean> => {
      return new Promise((resolve) => {
        const id = openModal({
          ...config,
          onOk: async () => {
            if (config.onOk) {
              await config.onOk();
            }
            resolve(true);
            closeModal(id);
          },
          onCancel: () => {
            if (config.onCancel) {
              config.onCancel();
            }
            resolve(false);
            closeModal(id);
          },
        });
      });
    },
    [openModal, closeModal]
  );

  return { confirm };
};

